/**
 * "Dá para vender aqui?" — FONTE ÚNICA da UI de compra.
 *
 * ── O que a regra é ─────────────────────────────────────────────────────────
 * Nos builds nativos (App Store / Play) a compra do PRO e do Founder passa
 * hoje pelo Stripe, na web. A diretriz 3.1.1 da Apple (e a equivalente do
 * Google) proíbe o app de levar a pessoa a um mecanismo de compra que não seja
 * o in-app purchase da plataforma — e isso inclui **botão, link e chamada para
 * ação**, não só a tela de pagamento. Enquanto o RevenueCat não estiver
 * plugado, a saída não é escrever "assine no site": esse texto é literalmente
 * o exemplo que a diretriz dá de infração. A saída é a UI de compra não
 * existir no app nativo.
 *
 * ⚠️ **Isto NÃO é "esconder o PRO".** Estado travado continua aparecendo — um
 * tema marcado "Só PRO", o selo de um recurso exclusivo — porque estado é
 * informação, não convite. O que sai é o que LEVA à compra.
 *
 * ── Por que um módulo, e não um `if` em cada tela ───────────────────────────
 * São 11 pontos de entrada para `/pro` e `/founder` hoje. Se cada um decidir
 * sozinho, um esquece — e o que vaza não é um detalhe visual, é uma
 * reprovação na revisão da loja, com dias de fila para tentar de novo. Pior:
 * o 12º ponto de entrada, escrito daqui a um mês por quem não leu isto, nasce
 * vazando. Por isso existem TRÊS camadas, e a terceira é a que segura o
 * futuro:
 *
 *   1. esta função, que responde a pergunta;
 *   2. o `AppRoutes`, que tira as rotas do ar no nativo — nenhum caminho leva
 *      ao checkout mesmo que um botão escape;
 *   3. `tests/unit/purchaseSurfaceGate.test.ts`, que varre `src/` e falha se
 *      aparecer uma navegação nova para essas rotas sem passar pelo portão.
 *
 * ── O RevenueCat entrou, e a resposta no nativo virou CONDICIONAL ───────────
 * Este é o ponto de integração previsto acima, agora ligado: no build nativo a
 * resposta deixou de ser um `false` fixo e passou a ser "há vitrine de IAP
 * ligada?" (`isNativePurchaseEnabled()`). Enquanto `VITE_IAP_PRODUCTS` estiver
 * vazio — que é o **default**, inclusive na suíte — o comportamento é byte a
 * byte o de antes: as 4 rotas fora do ar, nenhum CTA renderizado, os dois
 * guards verdes.
 *
 * ⚠️ O que mudou junto é QUEM cobra: com a flag ligada, quem vende no nativo é
 * a loja (RevenueCat → App Store / Play), nunca o Stripe. As três camadas
 * continuam existindo e continuam necessárias — elas passaram a proteger
 * "Stripe no nativo" em vez de "compra no nativo".
 */

import { isNativeApp } from "../../lib/platform/native";
import { isNativePurchaseEnabled } from "./commerce/iapProducts";

/**
 * `true` quando esta plataforma pode mostrar UI de compra.
 *
 * Web e PWA instalada: sempre `true` (vender no navegador é permitido, e a PWA
 * é navegador — confundir as duas tiraria a compra de quem instalou pela
 * tela de início, que é caminho legítimo e hoje converte).
 *
 * Build nativo: só com a vitrine de IAP ligada por flag **e** chave de API no
 * build. Desligado é o default.
 */
export function isPurchaseAvailable(): boolean {
  if (!isNativeApp()) return true;
  return isNativePurchaseEnabled();
}
